import {
  Archive,
  BadgeCheck,
  Copy,
  MapPin,
  MoreVertical,
  Package,
  Pencil,
  Trash2,
} from 'lucide-react';
import {
  isProjectVersion,
  PROJECT_STATUS_LABEL,
  type Project,
  type ProjectStatus,
} from '@obraplan/core/domain/structure/project';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/cn';
import { ProjectTerm } from './ProjectTerm';

const STATUS_TONE: Readonly<Record<ProjectStatus, 'neutral' | 'ok' | 'info' | 'warn'>> = {
  PLANNING: 'info',
  ACTIVE: 'ok',
  PAUSED: 'warn',
  FINISHED: 'neutral',
  ARCHIVED: 'neutral',
};

/**
 * Uma obra na lista, desenhada como a placa na entrada do canteiro: o código e a
 * situação de um lado, nome, cliente e endereço no meio, o prazo do outro. O que a placa
 * não tem — selecionar, editar, arquivar — fica na ponta.
 *
 * A obra ativa tem uma barra na borda esquerda e um fundo levemente tingido, além do
 * rótulo escrito: a cor acompanha, mas quem lê em preto e branco lê "Obra ativa".
 */
export function ProjectRow({
  project,
  today,
  isActive,
  canManage,
  nested = false,
  hasVersions = false,
  onSelect,
  onEdit,
  onArchive,
  onDuplicate,
  onApprove,
  onDeleteVersion,
}: {
  project: Project;
  /** O hoje **da obra**, pelo fuso dela (RN-123). */
  today: string;
  isActive: boolean;
  canManage: boolean;
  /** A linha de uma versão, desenhada abaixo da aprovada (07/10/2026). */
  nested?: boolean;
  /** A aprovada que tem versões abaixo: ganha o selo que diz quem a execução acompanha. */
  hasVersions?: boolean;
  onSelect: () => void;
  onEdit: () => void;
  onArchive: () => void;
  onDuplicate: () => void;
  onApprove: () => void;
  onDeleteVersion: () => void;
}) {
  const archived = project.status === 'ARCHIVED';
  const version = isProjectVersion(project);
  const place = [project.client, project.address].filter((part) => part !== undefined);

  return (
    <li
      className={cn(
        'relative grid gap-x-5 gap-y-3 px-4 py-4 md:grid-cols-[8rem_minmax(0,1fr)_15rem_auto] md:items-center md:px-5',
        nested && 'bg-(--color-surface-muted)/40 md:pl-12',
        isActive && 'bg-(--color-brand)/5',
      )}
    >
      {isActive && (
        <span className="absolute inset-y-0 left-0 w-1 bg-(--color-brand)" aria-hidden="true" />
      )}

      <div className="flex flex-row flex-wrap items-center gap-2 md:flex-col md:items-start md:gap-1.5">
        <span className="font-mono text-xs font-semibold uppercase tracking-[0.08em] text-(--color-content)">
          {project.code}
        </span>
        <Badge tone={STATUS_TONE[project.status]}>{PROJECT_STATUS_LABEL[project.status]}</Badge>
        {version ? (
          <Badge tone="warn">Versão de orçamento</Badge>
        ) : (
          hasVersions && (
            <Badge tone="ok" icon={<BadgeCheck aria-hidden="true" />}>
              Aprovada — a execução acompanha
            </Badge>
          )
        )}
      </div>

      <div className="flex min-w-0 flex-col gap-1">
        <p className="text-base font-semibold leading-tight md:truncate">{project.name}</p>
        {place.length > 0 ? (
          <p className="flex min-w-0 items-start gap-1.5 text-sm text-(--color-content-muted)">
            <MapPin className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
            <span className="min-w-0 md:truncate">{place.join(' · ')}</span>
          </p>
        ) : (
          <p className="text-sm text-(--color-content-muted)">Sem cliente nem endereço</p>
        )}
        {project.externalId !== undefined && (
          <p className="flex items-center gap-1.5 text-xs text-(--color-content-muted)">
            <Package className="size-3.5 shrink-0" aria-hidden="true" />
            Requisita material no destino <span className="font-mono">{project.externalId}</span>
          </p>
        )}
      </div>

      <ProjectTerm project={project} today={today} />

      <div className="flex items-center gap-2 md:justify-end">
        {isActive ? (
          <Badge tone="info" filled>
            Obra ativa
          </Badge>
        ) : archived ? (
          // Arquivada não se seleciona: para voltar ao dia a dia, edite a situação.
          <span className="text-xs text-(--color-content-muted)">Fora do dia a dia</span>
        ) : (
          <Button variant="secondary" size="sm" onClick={onSelect}>
            Selecionar
          </Button>
        )}
        {canManage && !archived && (
          <Button variant="ghost" size="sm" onClick={onDuplicate}>
            <Copy aria-hidden="true" />
            Duplicar obra
          </Button>
        )}
        {canManage && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" aria-label={`Ações de ${project.name}`}>
                <MoreVertical aria-hidden="true" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuItem onSelect={onEdit}>
                <Pencil aria-hidden="true" />
                Editar
              </DropdownMenuItem>
              {version && (
                <DropdownMenuItem onSelect={onApprove}>
                  <BadgeCheck aria-hidden="true" />
                  Aprovar versão
                </DropdownMenuItem>
              )}
              {version ? (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem destructive onSelect={onDeleteVersion}>
                    <Trash2 aria-hidden="true" />
                    Excluir versão
                  </DropdownMenuItem>
                </>
              ) : (
                !archived && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem destructive onSelect={onArchive}>
                      <Archive aria-hidden="true" />
                      Arquivar
                    </DropdownMenuItem>
                  </>
                )
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    </li>
  );
}
